// client/src/components/admin/advertiser/AdvertiserPointHistoryModal.js
import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

const TYPE_LABELS = {
  charge:           '포인트 충전',
  project_deposit:  '프로젝트 결제',
  project_refund:   '프로젝트 환불',
  refund_request:   '환불 신청',
  refund_approved:  '환불 승인',
  refund_rejected:  '환불 거절',
  point_adjustment: '포인트 조정',
};

const PERIOD_OPTIONS = [
  { value: '1month',  label: '1개월' },
  { value: '3months', label: '3개월' },
  { value: '6months', label: '6개월' },
  { value: '1year',   label: '1년' },
  { value: 'all',     label: '전체' },
];

function AdvertiserPointHistoryModal({ open, onClose, advertiser }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [currentPoints, setCurrentPoints] = useState(0);
  const [pendingRefundPoints, setPendingRefundPoints] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [period, setPeriod] = useState('3months');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback(async () => {
    if (!advertiser?.userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize: 15,
        type: filterType,
        period,
      });
      const res = await fetch(
        `${API_URL}/api/admin/advertiser/info/${advertiser.userId}/points/history?${params}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (data.ok) {
        setItems(data.items || []);
        setCurrentPoints(data.currentPoints || 0);
        setPendingRefundPoints(data.pendingRefundPoints || 0);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('fetchHistory error:', err);
    } finally {
      setLoading(false);
    }
  }, [advertiser?.userId, page, filterType, period]);

  // open 시 초기화 후 조회
  useEffect(() => {
    if (open) {
      setPage(1);
      setFilterType('all');
      setPeriod('3months');
    }
  }, [open]);

  // 필터/페이지 변경 시 조회
  useEffect(() => {
    if (open && advertiser?.userId) {
      fetchHistory();
    }
  }, [open, fetchHistory]);

  const handleFilterType = (newType) => {
    setFilterType(newType);
    setPage(1);
  };

  const handlePeriod = (newPeriod) => {
    setPeriod(newPeriod);
    setPage(1);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  if (!open || !advertiser) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '860px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', marginBottom: 6 }}>
              {advertiser.userName} ({advertiser.userId}) — 포인트 이력
            </div>
            <div style={{ display: 'flex', gap: 20, fontSize: 13, color: '#64748b' }}>
              <span>
                현재 잔액&nbsp;
                <strong style={{ color: '#2563eb', fontSize: 15 }}>
                  {currentPoints.toLocaleString()}pt
                </strong>
              </span>
              {pendingRefundPoints > 0 && (
                <span>
                  환불 처리 중&nbsp;
                  <strong style={{ color: '#f59e0b' }}>
                    {pendingRefundPoints.toLocaleString()}pt
                  </strong>
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: '#9ca3af', lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* 필터 */}
        <div style={{
          padding: '12px 24px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          gap: 12,
          flexWrap: 'wrap',
        }}>
          {/* 타입 필터 */}
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { value: 'all',    label: '전체' },
              { value: 'charge', label: '충전' },
              { value: 'use',    label: '사용' },
              { value: 'refund', label: '환불' },
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleFilterType(value)}
                style={{
                  padding: '5px 14px',
                  borderRadius: 20,
                  border: '1.5px solid',
                  borderColor: filterType === value ? '#2563eb' : '#d1d5db',
                  background: filterType === value ? '#2563eb' : '#fff',
                  color: filterType === value ? '#fff' : '#374151',
                  fontSize: 13,
                  fontWeight: filterType === value ? 700 : 400,
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 기간 필터 */}
          <select
            value={period}
            onChange={(e) => handlePeriod(e.target.value)}
            style={{
              padding: '5px 12px',
              borderRadius: 8,
              border: '1.5px solid #d1d5db',
              fontSize: 13,
              color: '#374151',
              cursor: 'pointer',
              background: '#fff',
            }}
          >
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {/* 테이블 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>불러오는 중...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>이력이 없습니다.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['일시', '유형', '메모', '금액', '잔액'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '12px 10px',
                        textAlign: h === '금액' || h === '잔액' ? 'right' : 'left',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: 12,
                        position: 'sticky',
                        top: 0,
                        background: '#fff',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isAdjustment = item.type === 'point_adjustment';
                  const isNegative = item.amount < 0;
                  return (
                    <tr
                      key={String(item._id)}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: isAdjustment ? '#fffbeb' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '11px 10px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {formatDate(item.createdAt)}
                      </td>
                      <td style={{ padding: '11px 10px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          background: isAdjustment
                            ? '#fef3c7'
                            : item.type === 'charge'
                            ? '#dbeafe'
                            : item.type === 'project_deposit'
                            ? '#fce7f3'
                            : '#f0fdf4',
                          color: isAdjustment
                            ? '#92400e'
                            : item.type === 'charge'
                            ? '#1d4ed8'
                            : item.type === 'project_deposit'
                            ? '#9d174d'
                            : '#166534',
                        }}>
                          {TYPE_LABELS[item.type] || item.type}
                        </span>
                      </td>
                      <td style={{ padding: '11px 10px', color: '#374151', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.memo || '-'}
                        {item.projectNumber && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>
                            #{item.projectNumber}
                          </span>
                        )}
                      </td>
                      <td style={{
                        padding: '11px 10px',
                        textAlign: 'right',
                        fontWeight: 700,
                        color: isNegative ? '#dc2626' : '#16a34a',
                        whiteSpace: 'nowrap',
                      }}>
                        {isNegative ? '' : '+'}{item.amount.toLocaleString()}pt
                      </td>
                      <td style={{
                        padding: '11px 10px',
                        textAlign: 'right',
                        color: '#374151',
                        whiteSpace: 'nowrap',
                      }}>
                        {item.balanceAfter != null ? `${item.balanceAfter.toLocaleString()}pt` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 + 하단 */}
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            총 {total.toLocaleString()}건
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              style={{
                padding: '5px 12px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: page <= 1 ? '#f9fafb' : '#fff',
                color: page <= 1 ? '#d1d5db' : '#374151',
                cursor: page <= 1 ? 'default' : 'pointer',
                fontSize: 13,
              }}
            >
              ◀ 이전
            </button>
            <span style={{ fontSize: 13, color: '#374151', minWidth: 60, textAlign: 'center' }}>
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              style={{
                padding: '5px 12px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: page >= totalPages ? '#f9fafb' : '#fff',
                color: page >= totalPages ? '#d1d5db' : '#374151',
                cursor: page >= totalPages ? 'default' : 'pointer',
                fontSize: 13,
              }}
            >
              다음 ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdvertiserPointHistoryModal;
